/**
 * A glossary of light-frame construction terms, shared by every plan type.
 *
 * One definition per term, tagged with the plan types it belongs to, and each type's
 * reference page (`reference/references.ts`) shows the terms tagged with it, in the
 * order that type is built. A joist is a joist under a shed and under a deck: written
 * twice it would be corrected in one place and not the other, so a second plan type adds
 * its tag to the terms it shares and writes only the ones it does not.
 *
 * Hand-curated learning material, and deliberately **generic**: nothing here reads
 * the engine, the spec, or the design on screen, and nothing here should ever say
 * "your shed". A page that describes the building someone is configuring is a build
 * guide — a different thing, with a different liability, reserved as a projector in
 * DESIGN §7 and gated on framing correctness. This is a page someone can read before
 * they have designed anything at all.
 *
 * Definitions are written to be true of light framing generally, not of one plan.
 */

import type { PlanType } from "../site/planTypes.js";

export interface Term {
  /** The term itself, as a framer says it. */
  term: string;
  /** The plan types whose reference lists it. */
  types: readonly PlanType[];
  /** Other names for the same thing, if it goes by more than one. */
  aka?: string[];
  /** What it is, in a sentence or two. */
  definition: string;
  /** Why it matters — the part a definition alone leaves out. */
  note?: string;
  /**
   * Terms worth reading next. Matched against `term`, case-insensitively, and tagged
   * with every type this one is, so a cross-reference never points off its page.
   */
  see?: string[];
}

export interface Section {
  id: string;
  title: string;
  blurb: string;
  terms: Term[];
}

export const GLOSSARY: Section[] = [
  {
    id: "walls",
    title: "Walls",
    blurb:
      "A framed wall is a ladder of vertical studs between two horizontal plates, " +
      "built flat on the deck and then stood up.",
    terms: [
      {
        term: "Stud",
        types: ["shed"],
        definition:
          "A vertical framing member in a wall, running from the bottom plate to the " +
          "top plate. Usually 2x4 or 2x6, spaced at a regular interval.",
        note:
          "The stud is the unit the rest of the wall is measured against: sheathing " +
          "lands on studs, and insulation fills the bays between them.",
        see: ["On-center", "Bottom plate", "Top plate"],
      },
      {
        term: "Bottom plate",
        types: ["shed"],
        aka: ["Sole plate", "Sill plate"],
        definition:
          "The horizontal member every stud in a wall stands on, nailed down to the " +
          "floor deck once the wall is raised.",
        note:
          'Called a "sill plate" when it sits directly on concrete, in which case it ' +
          "has to be pressure-treated and is usually bolted down rather than nailed.",
        see: ["Top plate"],
      },
      {
        term: "Top plate",
        types: ["shed"],
        definition:
          "The horizontal member across the top of a wall's studs. Light framing " +
          "normally uses two courses — a double top plate.",
        note:
          "The second course is not redundancy. It laps the corners to tie the walls " +
          "into a ring, and it bridges the joints in the course below it, so a plate " +
          "spliced mid-wall is still a continuous beam.",
        see: ["Cap plate", "Lap", "Stud"],
      },
      {
        term: "Cap plate",
        types: ["shed"],
        aka: ["Double top plate", "Second top plate"],
        definition:
          "The upper of the two top-plate courses. Its joints are deliberately offset " +
          "from the lower course's, conventionally by about four feet.",
        note:
          "Two courses breaking in the same bay make a hinge rather than a beam.",
        see: ["Top plate", "Lap"],
      },
      {
        term: "Lap",
        types: ["shed"],
        definition:
          "Where one member runs over another to tie them together — at a corner, one " +
          "wall's cap plate crosses over the other wall's top plate.",
        note:
          "A lap is one board over another. Two boards trying to occupy the same space " +
          "is an interference, and it is the sort of mistake that looks perfectly " +
          "convincing in a 3D view because coincident solids render as one.",
        see: ["Cap plate", "Corner post"],
      },
      {
        term: "Corner post",
        types: ["shed"],
        aka: ["California corner", "Three-stud corner"],
        definition:
          "The assembly of studs at the junction of two walls. It has to hold up the " +
          "corner and also give the inside surface something to fasten to.",
        note:
          'A "California corner" does that with two studs plus a short block instead ' +
          "of three solid studs, leaving room for insulation in the corner.",
        see: ["Stud", "Lap"],
      },
      {
        term: "Nailer",
        types: ["shed"],
        definition:
          "A stud added off the regular layout, usually nailed flat against another " +
          "piece, purely so something has wood to fasten to — most often a sheet edge " +
          "that would otherwise land in a bay.",
        note:
          "It is structurally idle. Where an opening's king stud stands close to a " +
          "layout mark, the mark cannot have a stud of its own without the two boards " +
          "occupying the same place, so the piece slides tight against the king and " +
          "catches the sheet edge from there.",
        see: ["Stud", "King stud", "Layout", "Sheathing"],
      },
      {
        term: "Blocking",
        types: ["shed"],
        definition:
          "Short pieces let in between studs or joists — to back a sheet edge, to stop " +
          "a bay from acting as a chimney, or to give something a place to fasten.",
        see: ["Sheathing"],
      },
    ],
  },
  {
    id: "layout",
    title: "Layout",
    blurb:
      "Where the members go. Layout is arithmetic done once, on the plate, and it is " +
      "what makes sheet goods land on wood.",
    terms: [
      {
        term: "On-center",
        types: ["shed"],
        aka: ["OC", "o.c."],
        definition:
          "Spacing measured from the centerline of one member to the centerline of the " +
          "next — 16\" on-center, 24\" on-center. Not the clear gap between them.",
        note:
          "Center-to-center is what makes the arithmetic work: a 4-foot sheet edge " +
          "falls at 48\", which is a whole number of 12\", 16\", and 24\" bays, so the " +
          "edge lands on the middle of a member and the next sheet lands on the other " +
          "half of it.",
        see: ["Layout", "Sheathing"],
      },
      {
        term: "Layout",
        types: ["shed"],
        definition:
          "The marks on a plate saying where each stud goes, made from one end of the " +
          "building with a tape run out once.",
        note:
          "The first member is flush with the end of the wall, so its center sits half " +
          "a thickness in — three quarters of an inch for a 2x. Every mark after it is " +
          "a whole multiple of the spacing from the *building's* corner. Hooking the " +
          "tape somewhere else instead — the end of one wall's own plate, say — throws " +
          "every sheet edge off by that much for the length of the building.",
        see: ["On-center", "Sheathing"],
      },
      {
        term: "Bay",
        types: ["shed"],
        definition: "The space between two adjacent framing members.",
        note:
          "Layout leaves one short bay at the far end, where the run stops dividing " +
          "evenly. That is correct: the odd bay gives, not the sheet layout.",
        see: ["Layout"],
      },
      {
        term: "Rough opening",
        types: ["shed"],
        aka: ["RO"],
        definition:
          "The hole in the framing a door or window unit sits in — bigger than the " +
          "unit itself, to leave room to shim it plumb and square.",
        see: ["Header", "King stud", "Jack stud"],
      },
    ],
  },
  {
    id: "openings",
    title: "Openings",
    blurb:
      "Cutting a hole in a wall interrupts the studs that were carrying load there. " +
      "Framing an opening is putting that path back.",
    terms: [
      {
        term: "Header",
        types: ["shed"],
        definition:
          "The beam over an opening, carrying the load the interrupted studs used to " +
          "carry and delivering it to the studs beside the opening.",
        note:
          "Sized by what it spans and what is above it. In 2x wall framing it is often " +
          "two plies on edge with a spacer between them, so the assembly comes out the " +
          "thickness of the wall.",
        see: ["Jack stud", "King stud", "Rough opening"],
      },
      {
        term: "Jack stud",
        types: ["shed"],
        aka: ["Trimmer"],
        definition:
          "The shortened stud beside an opening that the header bears on, running from " +
          "the bottom plate up to the underside of the header.",
        note:
          "Jacks are the legs of the beam. Their number goes up with the load the " +
          "header carries: where one would crush the plate under it - a wide opening " +
          "under a heavy roof, or a header a ridge post stands on - two stand under each " +
          "end, nailed face to face, and the king stud moves out past them. Each pair " +
          "takes a stud's more wall beside the opening.",
        see: ["Header", "King stud"],
      },
      {
        term: "King stud",
        types: ["shed"],
        definition:
          "The full-height stud immediately outside each jack, running plate to plate " +
          "and nailed to the jack beside it.",
        note:
          "The jack carries the header down; the king keeps the whole assembly from " +
          "rotating out of the wall and ties it back into the plates.",
        see: ["Jack stud", "Header"],
      },
      {
        term: "Cripple stud",
        types: ["shed"],
        aka: ["Cripple"],
        definition:
          "A short stud filling the space above a header or below a rough sill, on the " +
          "same layout the wall's full-height studs use.",
        note:
          "It keeps the layout unbroken across the opening, so sheathing above and " +
          "below the hole still finds framing where it expects to.",
        see: ["Rough sill", "Header", "Layout"],
      },
      {
        term: "Rough sill",
        types: ["shed"],
        definition:
          "The horizontal member forming the bottom of a window's rough opening. Doors " +
          "have none, because the opening runs to the floor.",
        see: ["Rough opening", "Cripple stud"],
      },
    ],
  },
  {
    id: "roof",
    title: "Roof",
    blurb:
      "The roof is where framing stops being rectangles. Every member meets something " +
      "at an angle, and its ends have to be cut to that angle to bear on it.",
    terms: [
      {
        term: "Pitch",
        types: ["shed"],
        aka: ["Slope"],
        definition:
          'How steep a roof is, given as rise over a 12" run: a "4:12" roof climbs ' +
          "4 vertical inches for every 12 horizontal.",
        note:
          "A ratio rather than an angle, because every cut on the roof is laid out " +
          "from that ratio with a square — nobody sets a framing square to 18.43°.",
        see: ["Rafter", "Plumb cut", "Seat cut"],
      },
      {
        term: "Rafter",
        types: ["shed"],
        definition:
          "A sloped member running from the ridge down to the wall, carrying the roof " +
          "sheathing and everything on it.",
        note:
          "A rafter's board is longer than the horizontal distance it covers, and " +
          "longer again than the sloped line it sits on — the angled cuts at each end " +
          "run diagonally across the stock. Ordering the line length gets you a rafter " +
          "short at both ends.",
        see: ["Plumb cut", "Seat cut", "Birdsmouth", "Ridge board"],
      },
      {
        term: "Ridge board",
        types: ["shed"],
        definition:
          "The board at the peak that the rafters from both slopes land against. It is " +
          "an alignment and nailing surface: with the rafters tied at the bottom it " +
          "carries almost no load, and it needs no support of its own.",
        note:
          "It has to be at least as deep as the cut end of the rafter, which is deeper " +
          "than the rafter itself — a plumb cut runs diagonally across the stock, so a " +
          "board only as deep as the rafter leaves part of that cut bearing on nothing. " +
          "And a ridge board is only honest where rafter ties are doing their job: " +
          "without them the same piece has to be a ridge beam instead.",
        see: ["Rafter", "Plumb cut", "Ridge beam", "Rafter tie"],
      },
      {
        term: "Ridge beam",
        types: ["shed"],
        definition:
          "A ridge sized and supported to actually carry half the roof, used when " +
          "nothing ties the rafter feet together — a cathedral or vaulted ceiling.",
        note:
          "It is a beam in the ordinary sense: sized for its span, not spliced, and " +
          "held up at each end by a post that runs continuously to a foundation. The " +
          "difference between this and a ridge board is the difference between a roof " +
          "that hangs from the peak and one that stands as a triangle.",
        see: ["Ridge board", "Rafter tie", "Thrust", "Post"],
      },
      {
        term: "Rafter tie",
        types: ["shed"],
        definition:
          "A horizontal member joining a pair of opposing rafters near the plate, " +
          "holding the bottoms of the two slopes together.",
        note:
          "It works in tension, resisting the outward thrust — so a fairly small board " +
          "carries a surprising amount, and its span matters far less than its " +
          "connections. It is not a ceiling joist and not a floor: an attic you can " +
          "store things in needs members sized for that, which is a different job. " +
          "The rafter pair at each gable end gets none: it stands on the gable wall's " +
          "studs along its whole length and pushes on nothing.",
        see: ["Thrust", "Collar tie", "Ridge board", "Top plate", "Gable"],
      },
      {
        term: "Thrust",
        types: ["shed"],
        definition:
          "The outward push a pitched roof puts on the walls it sits on. The shallower " +
          "the pitch, the harder it pushes for the same load.",
        note:
          "On a gable it has to be resisted by something, and there are only two " +
          "answers: tie the rafter feet together, or carry the ridge on a beam so the " +
          "rafters hang rather than lean. Doing neither looks finished and spreads the " +
          "walls over a few seasons. A lean-to's rafters are the exception: seated level " +
          "on a wall at each end, they push on neither.",
        see: ["Rafter tie", "Ridge beam", "Pitch", "Lean-to"],
      },
      {
        term: "Ground snow load",
        types: ["shed"],
        aka: ["Snow load"],
        definition:
          "The weight of snow a building department expects on the ground at a site, in " +
          "pounds per square foot, from maps drawn for its area. It ranges from nothing " +
          "in the South to well over a hundred in the high mountains.",
        note:
          "A roof is sized for it: rafters, a ridge beam, and the headers over doors and " +
          "windows all get deeper as it goes up, because the same span has to carry " +
          "more. The figure is local and only the building department has it, which is " +
          "why it is asked for rather than assumed.",
        see: ["Span", "Rafter", "Header"],
      },
      {
        term: "Post",
        types: ["shed"],
        definition:
          "A vertical member carrying a concentrated load straight down — under the end " +
          "of a beam, most often.",
        note:
          "The load has to keep going: a post that lands on a floor rather than on " +
          "something continuous to the ground has moved the problem rather than solved " +
          "it. Where a door or window is in the way, the post has to land on a header " +
          "sized for that load rather than for the opening.",
        see: ["Ridge beam", "Header", "Stud"],
      },
      {
        term: "Plumb cut",
        types: ["shed"],
        definition:
          "A cut that stands vertical once the member is installed. A rafter gets one " +
          "at the ridge, so it bears flat against the ridge board, and usually one at " +
          "the tail so the fascia has something flat to land on.",
        note: "Its angle is the roof's pitch, laid out with a square.",
        see: ["Pitch", "Rafter", "Seat cut"],
      },
      {
        term: "Seat cut",
        types: ["shed"],
        aka: ["Level cut"],
        definition:
          "The horizontal cut that lies flat on the top plate, carrying the rafter's " +
          "load into the wall. It is the level half of a birdsmouth.",
        see: ["Birdsmouth", "Heel cut", "Plumb cut"],
      },
      {
        term: "Birdsmouth",
        types: ["shed"],
        definition:
          "The notch cut into the underside of a rafter where it crosses the wall — a " +
          "level seat cut bearing on the top plate and a plumb heel cut down the " +
          "outside face of the wall.",
        note:
          "Without it a rafter merely touches the outer corner of the plate rather " +
          "than sitting on it. Cutting one lowers the whole roof by the height of the " +
          "heel. Convention limits the notch to about a third of the rafter's depth: " +
          "past that, what is left over the bearing point is no longer the rafter that " +
          "was sized for the span, so a steep roof on shallow stock takes a narrower " +
          "seat instead of a deeper notch.",
        see: ["Seat cut", "Heel cut", "Rafter", "Top plate"],
      },
      {
        term: "Heel cut",
        types: ["shed"],
        definition:
          "The plumb half of a birdsmouth: the vertical cut at the outer face of the " +
          "wall, from the rafter's underside up to the seat.",
        see: ["Birdsmouth", "Seat cut"],
      },
      {
        term: "Rafter tail",
        types: ["shed"],
        aka: ["Tail"],
        definition:
          "The part of the rafter that continues past the wall to form the eave " +
          "overhang.",
        see: ["Eave", "Fascia", "Overhang"],
      },
      {
        term: "Eave",
        types: ["shed"],
        definition:
          "The lower edge of a roof, where it overhangs the wall below it.",
        see: ["Rafter tail", "Fascia", "Rake"],
      },
      {
        term: "Rake",
        types: ["shed"],
        definition:
          "The sloped edge of a roof at a gable end — the edge that climbs from the " +
          "eave to the peak.",
        see: ["Gable", "Eave"],
      },
      {
        term: "Fascia",
        types: ["shed"],
        definition:
          "The board closing the ends of the rafter tails along an eave. The roof edge " +
          "and a gutter both land on it.",
        note:
          "It is what the plumb tail cut is *for*: a square-cut tail leaves a face " +
          "that is not vertical and nothing flat to nail the fascia to. It also has to " +
          "be deeper than the rafter's stock, because it covers the rafter's plumb-cut " +
          "end face.",
        see: ["Rafter tail", "Eave", "Plumb cut"],
      },
      {
        term: "Frieze block",
        types: ["shed"],
        aka: ["Bird block", "Bird blocking", "Eave blocking"],
        definition:
          "The block that fills a rafter bay where the rafters cross the wall, closing " +
          "the gap between the top plate and the roof sheathing.",
        note:
          "Without it every bay is an open slot into the roof space — a couple of " +
          "inches by the width of the bay, all the way along both eaves — and birds " +
          "and wasps find it quickly. It also stops the rafters from rolling on their " +
          "bearing, which is why the code asks for blocking once a rafter gets deep " +
          "enough for the question to matter. The ventilated version, sold as a vented " +
          "bird block, doubles as the intake for a vented roof space.",
        see: ["Blocking", "Top plate", "Birdsmouth", "Rip"],
      },
      {
        term: "Barge rafter",
        types: ["shed"],
        aka: ["Fly rafter"],
        definition:
          "The rafter out at the edge of a rake overhang, past the gable wall. It is " +
          "the board the roof edge and the rake trim land on.",
        note:
          "Nothing under it: it bears on no wall, which is why it takes no birdsmouth " +
          "and why something has to hold it up from the building. Nor is it tied " +
          "across to its opposite number: a tie there would hang outside the building " +
          "under the overhang.",
        see: ["Rake", "Outrigger", "Rafter", "Birdsmouth"],
      },
      {
        term: "Outrigger",
        types: ["shed"],
        aka: ["Lookout", "Outlooker"],
        definition:
          "A short piece running out past the gable wall to carry the barge rafter and " +
          "the rake overhang.",
        note:
          "Two ways to frame it. A gable ladder nails the pieces between the gable-end " +
          "rafter and the barge rafter like rungs, which suits a modest overhang. A " +
          "true outrigger runs back over the gable rafter to the next rafter in, which " +
          "carries much more — and needs the gable rafter dropped by the outrigger's " +
          "thickness so the sheathing still lands flat.",
        see: ["Barge rafter", "Rake", "Gable", "Blocking"],
      },
      {
        term: "Gable",
        types: ["shed"],
        definition:
          "The triangular section of wall between the two slopes of a roof, at the end " +
          "of the building.",
        note:
          "It is framed with studs of every different length, each cut to the underside " +
          "of the rafter above it. The rafter pair over it bears on those studs along " +
          "its whole length, which is why that pair takes neither a rafter tie nor a " +
          "collar tie.",
        see: ["Rake", "Rafter", "Lean-to", "Collar tie"],
      },
      {
        term: "Gable vent",
        types: ["shed"],
        aka: ["Louvered vent", "Gable louver"],
        definition:
          "A louvered, screened vent set high in a gable, one at each end, so air can " +
          "move through the space under the roof.",
        note:
          "A roof space closed at the eaves holds a summer's heat and a winter's damp, " +
          "and the damp ends up on the underside of the roof sheathing. Two vents let a " +
          "breeze through end to end. The hole is framed like a small window with no " +
          "header — a stud each side, a flat block over and under, and short studs " +
          "carrying the layout through — because nothing bears on it. Vents are sold by " +
          "the rough opening they fit, width by height, and the flange laps the siding " +
          "with the louvers sloping down and out to shed rain.",
        see: ["Gable", "Rough opening", "Cripple stud", "Frieze block"],
      },
      {
        term: "Lean-to",
        types: ["shed"],
        definition:
          "A roof of one slope across the whole building, rising from a low wall to a " +
          "high one — also called a shed roof. The two end walls climb with it, and " +
          "there is no ridge.",
        note:
          "Each rafter runs from one wall to the other and takes a birdsmouth at both: " +
          "down the outside face of the low wall and down the inside face of the high " +
          "one, which is framed taller by exactly the roof's climb between them. Seated " +
          "level at both ends, the rafters push on neither wall, so there are no ties " +
          "to fit. The price is span: a rafter crossing the whole width is twice as long " +
          "as a gable's, so a lean-to wants deeper rafters and tops out narrower. " +
          "Which side stands tall is only which way the building faces: turned " +
          "around, the high wall is on the other side and the door is in the other " +
          "end, with every piece the same.",
        see: ["Gable", "Birdsmouth", "Thrust", "Rafter", "Saltbox"],
      },
      {
        term: "Saltbox",
        types: ["shed"],
        definition:
          "A gable roof with its ridge off the middle: a long slope on one side and a " +
          "short one on the other, after the New England houses that grew a lean-to " +
          "down the back.",
        note:
          "With both slopes at one pitch, the wall under the short slope is framed " +
          "taller than the other by the difference in how far each climbs, so the two " +
          "arrive at the ridge together. The eaves are then at two heights and no " +
          "rafter tie can run level from one to the other, so the ridge is usually a " +
          "beam on posts, with its posts standing off the middle of each gable wall.",
        see: ["Gable", "Ridge beam", "Lean-to", "Rafter tie"],
      },
      {
        term: "Collar tie",
        types: ["shed"],
        definition:
          "A horizontal member connecting a pair of opposing rafters in the upper third " +
          "of the roof, resisting the ridge being pushed apart under wind uplift.",
        note:
          "Not the same as a rafter tie, and not a substitute for one. A tie up in the " +
          "top third barely resists the walls spreading — the force in it climbs the " +
          "higher it goes — which is why the two are different members with different " +
          "names doing different jobs. The residential code asks for them no more than " +
          "4 feet apart, which on rafters at 24 inches is every other pair, and accepts " +
          "a metal ridge strap in place of one. They go on the pairs between the gable " +
          "walls only. The pair at a gable end stands on the gable studs and is " +
          "sheathed into the gable along its whole length, so nothing pulls it apart at " +
          "the ridge; and a barge rafter hangs outside the wall on the rake framing, " +
          "where a tie would sit out under the overhang in the weather.",
        see: ["Rafter", "Ridge board", "Rafter tie", "Thrust", "Ridge strap", "Barge rafter"],
      },
      {
        term: "Ridge strap",
        types: ["shed"],
        definition:
          "A flat metal strap nailed across the top of a rafter pair at the ridge, " +
          "tying the two rafters to each other over the ridge board.",
        note:
          "It does a collar tie's job — holding the pair together at the ridge when " +
          "wind lifts the roof — without a board crossing the space under it, and the " +
          "residential code accepts one in place of a collar tie.",
        see: ["Collar tie", "Ridge board", "Rafter"],
      },
      {
        term: "Overhang",
        types: ["shed"],
        definition:
          "How far the roof projects past the wall. Measured horizontally, not along " +
          "the slope.",
        see: ["Eave", "Rake", "Rafter tail"],
      },
    ],
  },
  {
    id: "floor",
    title: "Floor and foundation",
    blurb: "What the walls stand on, and what that stands on.",
    terms: [
      {
        term: "Joist",
        types: ["shed"],
        definition:
          "A horizontal member carrying a floor, spanning between supports and spaced " +
          "on-center like studs.",
        see: ["Rim joist", "Span", "On-center"],
      },
      {
        term: "Rim joist",
        types: ["shed"],
        aka: ["Band joist"],
        definition:
          "The joist running around the perimeter, closing the ends of the joists and " +
          "holding them upright.",
        see: ["Joist"],
      },
      {
        term: "Skid",
        types: ["shed"],
        aka: ["Runner"],
        definition:
          "A heavy timber running under the floor frame, spreading the load onto the " +
          "ground and letting a small building be dragged.",
        note:
          "The usual foundation for a shed small enough to be exempt from permitting.",
        see: ["Joist"],
      },
      {
        term: "Pier block",
        types: ["shed"],
        aka: ["Deck block"],
        definition:
          "A precast concrete block set on the ground to carry one bearing point of a " +
          "floor. A row of them under a beam is a pier foundation.",
        note:
          "How far apart they go is a question about the beam over them, not about the " +
          "blocks: the beam's span is what decides it.",
        see: ["Floor beam", "Span"],
      },
      {
        term: "Floor beam",
        types: ["shed"],
        definition:
          "A built-up member spanning between piers and carrying the joists. Built up " +
          "means several plies of ordinary lumber nailed face to face.",
        note:
          "A joint in a beam belongs directly over a bearing point, and the plies are " +
          "staggered so they never all break in the same place.",
        see: ["Pier block", "Joist", "Span"],
      },
      {
        term: "Slab on grade",
        types: ["shed"],
        definition:
          "A single pour of concrete that is both the foundation and the finished " +
          "floor. There is no floor frame over it and nothing under it but fill.",
        see: ["Sill plate"],
      },
      {
        term: "Sill plate",
        types: ["shed"],
        aka: ["Mudsill"],
        definition:
          "The flat board a wall bears on where it meets concrete, bolted down into " +
          "the pour rather than nailed to a floor frame.",
        note:
          "Pressure treated, always: untreated lumber against concrete draws moisture " +
          "and rots from the bottom, where nothing shows until the wall moves.",
        see: ["Slab on grade", "Bottom plate", "Anchor bolt"],
      },
      {
        term: "Anchor bolt",
        types: ["shed"],
        definition:
          "A threaded rod set into wet concrete, left standing proud, and used to bolt " +
          "the sill plate down once the pour has cured.",
        note:
          "Placed close to every end of a plate and at intervals along it. Set them in " +
          "the wrong place and the plate has to be drilled to match.",
        see: ["Sill plate"],
      },
      {
        term: "Span",
        types: ["shed"],
        definition:
          "The clear distance a member has to cross between supports. What size a joist " +
          "or rafter needs to be is a question about span, spacing, and load.",
        note:
          "Published span tables answer it. They are the reason a wider building gets " +
          "deeper joists rather than more of the same ones.",
        see: ["Joist", "Rafter"],
      },
      {
        term: "Subfloor",
        types: ["shed"],
        aka: ["Deck"],
        definition:
          "The sheet goods laid over the joists, forming the floor surface and tying " +
          "the frame together.",
        see: ["Sheathing", "Joist"],
      },
    ],
  },
  {
    id: "sheet",
    title: "Sheet goods and skin",
    blurb: "The panels that close the frame in and stiffen it.",
    terms: [
      {
        term: "Sheathing",
        types: ["shed"],
        definition:
          "Structural panels — plywood or OSB — fastened over framing to make the wall " +
          "or roof act as one surface rather than a set of separate sticks.",
        note:
          "Every panel edge has to land on framing or on blocking. An unsupported butt " +
          "joint in a roof is a soft spot underfoot and a line the roofing telegraphs.",
        see: ["On-center", "Blocking", "Layout"],
      },
      {
        term: "Board foot",
        types: ["shed"],
        definition:
          "A lumber volume unit: nominal thickness × nominal width in inches × length " +
          "in feet, divided by 12.",
        note:
          "Quoted on the nominal size, which is the fiction the trade prices by — a " +
          '"2x4" is actually 1 1/2" by 3 1/2", but it is sold as a 2x4.',
        see: ["Nominal size"],
      },
      {
        term: "Nominal size",
        types: ["shed"],
        definition:
          'The name a piece of lumber is sold under, not its measurement. A "2x4" is ' +
          '1 1/2" by 3 1/2" once it has been dried and planed.',
        note:
          "The difference is not a rounding error to correct — it is what the material " +
          "actually is, and every layout number comes off the real dimension.",
        see: ["Board foot"],
      },
      {
        term: "Rip",
        types: ["shed"],
        definition:
          "A cut down the length of a board, with the grain, making it narrower. " +
          "Cutting across the board to length is a crosscut instead.",
        note:
          "Most framing is used at the width it is bought. A frieze block is not: the " +
          "gap it fills is whatever the rafter depth and the birdsmouth leave, which " +
          "is never a stock width, so it gets ripped to fit.",
        see: ["Bevel", "Frieze block", "Nominal size"],
      },
      {
        term: "Bevel",
        types: ["shed"],
        definition:
          "A cut made with the saw tilted, so the cut face is not square to the board. " +
          "A cut made with the blade square but the board turned is a miter.",
        note:
          "The top of a frieze block is beveled to the roof's pitch so the sheathing " +
          "lands flat on it rather than on one corner.",
        see: ["Rip", "Pitch", "Plumb cut"],
      },
      {
        term: "Kerf",
        types: ["shed"],
        definition:
          "The width of material a saw blade turns to sawdust — a strong 1/8\" for a " +
          "framing blade.",
        note:
          "It sounds like a detail until a board is cut into eight pieces and comes up " +
          "an inch short.",
      },
    ],
  },
];

/** Every term, flattened, for lookup and cross-referencing. */
export const ALL_TERMS: Term[] = GLOSSARY.flatMap((section) => section.terms);

/** A stable anchor id for a term, so a cross-reference can link to it. */
export function slugOf(term: string): string {
  return term.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
